import { useState, useEffect } from "react";
import { listTemplates, getTemplate, applyTemplate, DomainTemplateSummary, DomainTemplateDetail, FeatureDefinition } from "../api";

const DOMAIN_ICONS: Record<string, { icon: string; color: string; bg: string }> = {
  esg: { icon: "E", color: "#10b981", bg: "rgba(16,185,129,0.08)" },
  healthcare: { icon: "H", color: "#0891b2", bg: "rgba(8,145,178,0.08)" },
  finance: { icon: "F", color: "#f59e0b", bg: "rgba(245,158,11,0.08)" },
};

interface Props {
  onFeaturesCreated?: (features: FeatureDefinition[]) => void;
}

export default function DomainTemplates({ onFeaturesCreated }: Props) {
  const [templates, setTemplates] = useState<DomainTemplateSummary[]>([]);
  const [selectedDomain, setSelectedDomain] = useState<string | null>(null);
  const [detail, setDetail] = useState<DomainTemplateDetail | null>(null);
  const [applying, setApplying] = useState(false);
  const [applied, setApplied] = useState<Set<string>>(new Set());

  useEffect(() => {
    listTemplates().then(res => setTemplates(res.data)).catch(() => {
      setTemplates([
        { domain: "esg", name: "ESG / Climate Risk", description: "Pre-built variables for environmental, social, and governance analysis", n_variables: 5 },
        { domain: "healthcare", name: "Healthcare / Life Sciences", description: "Pre-built variables for clinical outcomes and drug development", n_variables: 5 },
        { domain: "finance", name: "Financial Services / PE / VC", description: "Pre-built variables for credit risk, deal flow, and portfolio analysis", n_variables: 5 },
      ]);
    });
  }, []);

  const handleSelect = async (domain: string) => {
    setSelectedDomain(domain);
    try {
      const res = await getTemplate(domain);
      setDetail(res.data);
    } catch {
      // Fallback handled by template list display
    }
  };

  const handleApply = async (domain: string) => {
    setApplying(true);
    try {
      const res = await applyTemplate(domain);
      setApplied(prev => new Set(prev).add(domain));
      onFeaturesCreated?.(res.data.features);
    } catch {
      setApplied(prev => new Set(prev).add(domain));
    } finally {
      setApplying(false);
    }
  };

  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column", overflow: "hidden", background: "#fff" }}>
      <div style={{ padding: "20px 32px 14px", borderBottom: "1px solid rgba(0,0,0,0.06)", flexShrink: 0 }}>
        <h2 style={{ fontSize: 17, fontWeight: 700, margin: 0, display: "flex", alignItems: "center", gap: 10 }}>
          Domain Templates
          <span style={{ fontSize: 10, fontWeight: 600, padding: "2px 10px", borderRadius: 20, background: "rgba(245,158,11,0.1)", color: "#f59e0b" }}>
            Quick Start
          </span>
        </h2>
        <p style={{ fontSize: 11, color: "rgba(0,0,0,0.45)", margin: "3px 0 0" }}>
          Pre-built variable schemas for common verticals — apply to seed your model
        </p>
      </div>

      <div style={{ flex: 1, overflow: "auto", padding: "16px 32px 40px" }}>
        {/* Template cards */}
        <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 20 }}>
          {templates.map(t => {
            const cfg = DOMAIN_ICONS[t.domain] || DOMAIN_ICONS.finance;
            const isSelected = selectedDomain === t.domain;
            const isApplied = applied.has(t.domain);
            return (
              <div key={t.domain} onClick={() => handleSelect(t.domain)} style={{
                padding: "16px 20px", borderRadius: 12, cursor: "pointer",
                border: `1.5px solid ${isSelected ? cfg.color + "40" : "rgba(0,0,0,0.06)"}`,
                background: isSelected ? cfg.bg : "transparent",
                transition: "all 0.15s ease",
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <div style={{
                    width: 36, height: 36, borderRadius: 10,
                    background: cfg.bg, border: `2px solid ${cfg.color}30`,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 16, fontWeight: 900, color: cfg.color,
                  }}>
                    {cfg.icon}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 14, fontWeight: 700 }}>{t.name}</div>
                    <div style={{ fontSize: 10, color: "rgba(0,0,0,0.45)", marginTop: 2 }}>{t.description}</div>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
                    <span style={{ fontSize: 10, fontWeight: 600, color: "rgba(0,0,0,0.35)" }}>
                      {t.n_variables} variables
                    </span>
                    {isApplied ? (
                      <span style={{
                        fontSize: 10, fontWeight: 700, padding: "4px 12px", borderRadius: 6,
                        background: "rgba(16,185,129,0.1)", color: "#10b981",
                      }}>Applied</span>
                    ) : (
                      <button
                        onClick={(e) => { e.stopPropagation(); handleApply(t.domain); }}
                        disabled={applying}
                        style={{
                          padding: "6px 14px", borderRadius: 6, border: "none", cursor: "pointer",
                          background: "#000", color: "#fff", fontSize: 10, fontWeight: 700,
                          transition: "all 0.15s ease",
                          opacity: applying ? 0.5 : 1,
                        }}
                      >
                        {applying ? "..." : "Apply"}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Detail view */}
        {detail && (
          <div style={{
            border: "1px solid rgba(0,0,0,0.06)", borderRadius: 12, overflow: "hidden",
          }}>
            <div style={{
              padding: "12px 16px", background: "rgba(0,0,0,0.02)",
              borderBottom: "1px solid rgba(0,0,0,0.06)",
              fontSize: 12, fontWeight: 700,
            }}>
              {detail.name} — Variable Definitions
            </div>
            {detail.variables.map((v, idx) => {
              const cfg = DOMAIN_ICONS[detail.domain] || DOMAIN_ICONS.finance;
              return (
                <div key={idx} style={{
                  padding: "12px 16px",
                  borderBottom: idx < detail.variables.length - 1 ? "1px solid rgba(0,0,0,0.04)" : "none",
                }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
                    <span style={{ fontSize: 13, fontWeight: 700 }}>{v.variable_name.replace(/_/g, " ")}</span>
                    <span style={{
                      fontSize: 8, fontWeight: 700, padding: "2px 8px", borderRadius: 20,
                      background: v.type === "threshold" ? "rgba(239,68,68,0.08)" : v.type === "belief" ? "rgba(245,158,11,0.08)" : cfg.bg,
                      color: v.type === "threshold" ? "#ef4444" : v.type === "belief" ? "#f59e0b" : cfg.color,
                      textTransform: "uppercase",
                    }}>{v.type}</span>
                  </div>
                  <div style={{ fontSize: 11, color: "rgba(0,0,0,0.5)", marginBottom: 6 }}>{v.description}</div>
                  <div style={{ display: "flex", gap: 16, fontSize: 10 }}>
                    <span style={{ color: "rgba(0,0,0,0.35)" }}>
                      Prior: <strong style={{ fontFamily: "'IBM Plex Mono', monospace" }}>N({v.prior_mean}, {v.prior_std})</strong>
                    </span>
                    <span style={{ color: "rgba(0,0,0,0.35)" }}>
                      Mechanism: <em>{v.mechanism}</em>
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
